import { TICKER_STOPLIST } from '@edge/edge-ai'

/**
 * Cashtag highlighting for chat text (Chris, 2026-08-28/29): ticker symbols
 * render with a `$` prefix, ALWAYS UPPERCASE, in a subtle highlight chip.
 *
 * Three matching layers, strongest signal first:
 *  1. Explicit cashtags (`$qnrx`, `$AAPL`) — any case, always highlight.
 *  2. Ticker-shaped tokens: ALL-CAPS (`QNRX`), plus NEAR-CAPS for STT
 *     artifacts (`QNRx` — voice transcription often lowercases a trailing
 *     letter). Both pass the acronym stoplists.
 *  3. KNOWN symbols in any case (`qnrx` in a lowercase-styled AI answer):
 *     once a symbol is established — anywhere in the conversation (threaded
 *     in via `known`) or earlier in the same text — its other-case
 *     occurrences highlight too, guarded against common English words.
 */

/** Extra all-caps words common in ANSWER prose that are never tickers. */
const PROSE_STOPLIST = new Set([
  'CTO',
  'COO',
  'GDP',
  'CPI',
  'PPI',
  'FOMC',
  'SMA',
  'EMA',
  'MACD',
  'VWAP',
  'HOD',
  'LOD',
  'IMO',
  'FYI',
  'ITM',
  'OTM',
  'ATM',
  'LLC',
  'INC',
  'EST',
  'EDT',
  'UTC',
  'AM',
  'YTD',
  'QOQ',
  'FAQ',
  'URL',
  'PDF',
  'CSV',
  'NASDAQ',
  'AMEX',
  'OTC',
  'NOT',
  'AND',
  'THE',
  'FOR',
  'ALL',
  'NEW',
  'NOW',
])

/**
 * Common 3-5 letter English words — consulted ONLY for the known-symbol
 * lowercase pass (layer 3). Real tickers collide with real words (ALL, PLAY,
 * OPEN…): an ALL-CAPS or `$` mention still highlights those, but a
 * lowercase "all" in prose must never light up just because $ALL came up.
 */
const COMMON_WORDS = new Set([
  'ARE',
  'WAS',
  'HAS',
  'HAD',
  'BUT',
  'CAN',
  'OUT',
  'GET',
  'SEE',
  'OWN',
  'TOO',
  'ANY',
  'DAY',
  'WAY',
  'USE',
  'BIG',
  'LOW',
  'ONE',
  'TWO',
  'YOU',
  'YES',
  'HIGH',
  'OPEN',
  'PLAY',
  'LOVE',
  'GOOD',
  'BEST',
  'REAL',
  'MOVE',
  'NEXT',
  'LAST',
  'WEEK',
  'HOLD',
  'WELL',
  'EVER',
  'ALSO',
  'JUST',
  'LIKE',
  'ONLY',
  'SOME',
  'THAT',
  'THEM',
  'THEN',
  'THEY',
  'THIS',
  'WITH',
  'WHAT',
  'WHEN',
  'MORE',
  'MOST',
  'DOWN',
  'VERY',
  'MUCH',
  'SUCH',
  'HERE',
  'SAID',
  'EACH',
  'CLOSE',
  'ABOUT',
  'AFTER',
  'TODAY',
  'STILL',
  'SINCE',
  'WOULD',
  'COULD',
  'THERE',
  'WHICH',
  'PRICE',
  'STOCK',
])

function passesStoplists(upperBase: string): boolean {
  return !TICKER_STOPLIST.has(upperBase) && !PROSE_STOPLIST.has(upperBase)
}

/** Tailwind classes for a highlighted cashtag span (shared by both renderers). */
export const CASHTAG_CLASS = 'rounded bg-primary/10 px-0.5 font-medium text-primary'

// One scan: group 1 = explicit cashtag symbol, group 2 = any alpha token of
// ticker shape (classified below). Nothing glued to digits/hyphens/dots on
// either side ("Form 25-NSE", "T-1").
const TOKEN_RE =
  /\$([A-Za-z]{1,5}(?:\.[A-Za-z]{1,2})?)\b|(?<![\w$.-])([A-Za-z]{2,5}(?:\.[A-Za-z]{1,2})?)(?![\w-])/g

/**
 * Classify a bare (non-`$`) token. `known` holds established UPPERCASE
 * symbols; returns true when the token should highlight as a ticker.
 */
function bareTokenIsTicker(token: string, known?: ReadonlySet<string>): boolean {
  const upper = token.toUpperCase()
  const upperBase = upper.split('.')[0]!
  if (!passesStoplists(upperBase)) return false

  // ALL-CAPS: the classic strong signal.
  if (token === upper) return true

  const letters = token.replace('.', '')
  const capCount = (letters.match(/[A-Z]/g) ?? []).length
  const lowerCount = letters.length - capCount

  // NEAR-CAPS (STT artifact, e.g. "QNRx"): mostly caps, exactly one
  // lowercase letter that is NOT a trailing plural-s ("ETFs", "EVs").
  if (capCount >= 2 && lowerCount === 1 && !/s$/.test(token)) return true

  // KNOWN symbol in any case (lowercase-styled AI answers): needs the
  // conversation to have established it, length ≥3, and no English-word
  // collision.
  if (known?.has(upper) && upper.length >= 3 && !COMMON_WORDS.has(upperBase)) return true

  return false
}

// A clock time right before a token ("6:34 a.m.", "9:30 AM", "16:00", "4 p.m."),
// an opening parenthesis allowed ("9:30 AM (ET)"). A bare number is not one:
// "bought 100 ET" is shares of ET.
const CLOCK_TIME_BEFORE_RE =
  /\b(?:\d{1,2}:\d{2}(?:\s*[ap]\.?\s?m\.?)?|\d{1,2}\s*[ap]\.?\s?m\.?)[\s(]*$/i

/**
 * ET right after a clock time is the time zone ("As of 6:34 a.m. ET"), with or
 * without a `$`, not Energy Transfer. ET anywhere else still highlights.
 */
function isTimeZone(text: string, match: RegExpExecArray): boolean {
  const symbol = match[1] ?? match[2]
  return symbol?.toUpperCase() === 'ET' && CLOCK_TIME_BEFORE_RE.test(text.slice(0, match.index))
}

export interface CashtagToken {
  type: 'text' | 'ticker'
  /** For tickers: normalized display, always `$SYMBOL`. */
  text: string
}

/**
 * Symbols this text establishes on its own (explicit cashtags + strict
 * ALL-CAPS/NEAR-CAPS matches), uppercased. Union across a conversation to
 * build the `known` set that lets lowercase mentions highlight.
 */
export function collectTickers(text: string): Set<string> {
  const out = new Set<string>()
  for (const match of text.matchAll(TOKEN_RE)) {
    if (isTimeZone(text, match)) continue
    const [, cashSym, bareSym] = match
    if (cashSym) {
      // Explicit cashtags always establish the symbol (stoplisted ones like
      // $AI still can't leak into lowercase prose — layer 3 re-checks the
      // stoplists before highlighting a bare token).
      out.add(cashSym.toUpperCase())
    } else if (bareSym && bareTokenIsTicker(bareSym)) {
      out.add(bareSym.toUpperCase())
    }
  }
  return out
}

/**
 * Split text into plain segments and ticker tokens. Self-sufficient: the
 * text's own strict matches join the caller's `known` set, so "QNRX opened…
 * later qnrx faded" highlights both without any threading.
 */
export function splitCashtags(text: string, known?: ReadonlySet<string>): CashtagToken[] {
  // Layer-3 lookup: caller's conversation-wide set + this text's own strict matches.
  const selfKnown = collectTickers(text)
  const lookup: ReadonlySet<string> = known ? new Set([...known, ...selfKnown]) : selfKnown

  const tokens: CashtagToken[] = []
  let last = 0
  for (const match of text.matchAll(TOKEN_RE)) {
    const [raw, cashSym, bareSym] = match
    const symbol = cashSym ?? bareSym
    if (!symbol || (bareSym && !bareTokenIsTicker(bareSym, lookup))) continue
    if (isTimeZone(text, match)) continue
    if (match.index > last) tokens.push({ type: 'text', text: text.slice(last, match.index) })
    tokens.push({ type: 'ticker', text: `$${symbol.toUpperCase()}` })
    last = match.index + raw.length
  }
  if (last < text.length) tokens.push({ type: 'text', text: text.slice(last) })
  return tokens
}

// ── Rehype plugin (assistant markdown) ──────────────────────────────────────

interface HastText {
  type: 'text'
  value: string
}

interface HastElement {
  type: string
  tagName?: string
  properties?: Record<string, unknown>
  children?: HastNode[]
}

type HastNode = HastText | HastElement

/** Tags whose text must never be rewritten (verbatim content / own styling). */
const SKIP_TAGS = new Set(['code', 'pre', 'a'])

function transform(node: HastNode, known?: ReadonlySet<string>): void {
  if (node.type !== 'element' && node.type !== 'root') return
  const el = node as HastElement
  if (el.tagName && SKIP_TAGS.has(el.tagName)) return
  if (!el.children) return

  const next: HastNode[] = []
  for (const child of el.children) {
    if (child.type === 'text') {
      const tokens = splitCashtags((child as HastText).value, known)
      if (tokens.some((t) => t.type === 'ticker')) {
        for (const token of tokens) {
          if (token.type === 'text') {
            next.push({ type: 'text', value: token.text })
          } else {
            next.push({
              type: 'element',
              tagName: 'span',
              properties: { className: CASHTAG_CLASS },
              children: [{ type: 'text', value: token.text }],
            })
          }
        }
        continue
      }
    }
    transform(child, known)
    next.push(child)
  }
  el.children = next
}

/**
 * Rehype plugin: wrap ticker symbols in highlighted `$SYMBOL` spans across
 * the rendered markdown (code, pre, and links excluded). Opt-in via
 * AppMarkdown's `highlightTickers`; `known` carries the conversation's
 * established symbols so lowercase mentions highlight too.
 */
export function rehypeCashtags(options?: { known?: ReadonlySet<string> }) {
  return (tree: unknown) => {
    transform(tree as HastNode, options?.known)
  }
}
